import { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import { useFlexibleList } from './useSWR';
import { API_BASE_URL, } from '../config/apiConfig';

/**
 * SWR hooks for file attachment operations
 */

// API_BASE_URL is now imported from centralized config

// Fetch all file attachments
export const useFileAttachments = () => {
  return useFlexibleList('/file-attachment');
};

// Fetch file attachments for a specific entity
export const useEntityFileAttachments = (entityTypeId, entityId) => {
  const key = entityTypeId && entityId 
    ? `/file-attachment/entity/${entityTypeId}/${entityId}`
    : null;
  
  return useFlexibleList(key);
};

// Fetch a single file attachment by ID
export const useFileAttachment = (id) => {
  const key = id ? `/file-attachment/${id}` : null;
  
  return useFlexibleList(key);
};

// Fetch file attachment statistics
export const useFileAttachmentStats = () => {
  return useFlexibleList('/file-attachment/stats');
};

// Create file attachment mutation
export const useCreateFileAttachment = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment', async (url, { arg }) => {
    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      },
      body: JSON.stringify(arg)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const createFileAttachment = async (fileAttachmentData) => {
    try {
      const result = await trigger(fileAttachmentData);
      
      // Invalidate related queries
      if (fileAttachmentData.entityTypeId && fileAttachmentData.entityId) {
        mutate(`/file-attachment/entity/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
        // Invalidate audit logs for the entity
        mutate(`/audit-log/master/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
      }
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error creating file attachment:', error);
      throw error;
    }
  };

  return {
    createFileAttachment,
    isCreating: isMutating,
    error
  };
};

// Upload file with actual file content
export const useUploadFile = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment/upload', async (url, { arg }) => {
    const { file, ...fileAttachmentData } = arg;
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Add other data as form fields (not JSON strings)
    Object.keys(fileAttachmentData).forEach(key => {
      if (fileAttachmentData[key] !== undefined && fileAttachmentData[key] !== null) {
        // Convert to string for FormData, but don't JSON.stringify
        formData.append(key, String(fileAttachmentData[key]));
      }
    });

    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const uploadFile = async (file, fileAttachmentData) => {
    try {
      const result = await trigger({ file, ...fileAttachmentData });
      
      // Invalidate related queries
      if (fileAttachmentData.entityTypeId && fileAttachmentData.entityId) {
        mutate(`/file-attachment/entity/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
        // Invalidate audit logs for the entity
        mutate(`/audit-log/master/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
      }
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  return {
    uploadFile,
    isUploading: isMutating,
    error
  };
};

// Upload multiple files
export const useUploadMultipleFiles = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment/upload-multiple', async (url, { arg }) => {
    const { files, masterEntityTypeId, ...fileAttachmentData } = arg;
    
    const formData = new FormData();
    
    // Add all files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add other data as form fields (not JSON strings)
    Object.keys(fileAttachmentData).forEach(key => {
      if (fileAttachmentData[key] !== undefined && fileAttachmentData[key] !== null) {
        // Convert to string for FormData, but don't JSON.stringify
        formData.append(key, String(fileAttachmentData[key]));
      }
    });

    // Add masterEntityTypeId to form data if provided
    if (masterEntityTypeId !== undefined && masterEntityTypeId !== null) {
      formData.append('masterEntityTypeId', String(masterEntityTypeId));
    }

    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'POST',
      headers: {
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const uploadMultipleFiles = async (files, fileAttachmentData) => {
    try {
      const result = await trigger({ files, ...fileAttachmentData });
      
      // Invalidate related queries
      if (fileAttachmentData.entityTypeId && fileAttachmentData.entityId) {
        mutate(`/file-attachment/entity/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
        // Invalidate audit logs for the entity
        mutate(`/audit-log/master/${fileAttachmentData.entityTypeId}/${fileAttachmentData.entityId}`);
      }
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      throw error;
    }
  };

  return {
    uploadMultipleFiles,
    isUploading: isMutating,
    error
  };
};

// Update file attachment mutation
export const useUpdateFileAttachment = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment', async (url, { arg }) => {
    const { id, ...updateData } = arg;
    
    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/file-attachment/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const updateFileAttachment = async (id, updateData) => {
    try {
      const result = await trigger({ id, ...updateData });
      
      // Invalidate related queries
      if (updateData.entityTypeId && updateData.entityId) {
        mutate(`/file-attachment/entity/${updateData.entityTypeId}/${updateData.entityId}`);
      }
      mutate(`/file-attachment/${id}`);
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error updating file attachment:', error);
      throw error;
    }
  };

  return {
    updateFileAttachment,
    isUpdating: isMutating,
    error
  };
};

// Delete file attachment mutation
export const useDeleteFileAttachment = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment', async (url, { arg }) => {
    const { id } = arg;
    
    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/file-attachment/${id}`, {
      method: 'DELETE',
      headers: {
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const deleteFileAttachment = async (id, entityTypeId, entityId) => {
    try {
      const result = await trigger({ id });
      
      // Invalidate related queries
      if (entityTypeId && entityId) {
        mutate(`/file-attachment/entity/${entityTypeId}/${entityId}`);
        // Invalidate audit logs for the entity
        mutate(`/audit-log/master/${entityTypeId}/${entityId}`);
      }
      mutate(`/file-attachment/${id}`);
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error deleting file attachment:', error);
      throw error;
    }
  };

  return {
    deleteFileAttachment,
    isDeleting: isMutating,
    error
  };
};

// Delete all file attachments for an entity
export const useDeleteEntityFileAttachments = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment', async (url, { arg }) => {
    const { entityTypeId, entityId } = arg;
    
    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/file-attachment/entity/${entityTypeId}/${entityId}`, {
      method: 'DELETE',
      headers: {
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const deleteEntityFileAttachments = async (entityTypeId, entityId) => {
    try {
      const result = await trigger({ entityTypeId, entityId });
      
      // Invalidate related queries
      mutate(`/file-attachment/entity/${entityTypeId}/${entityId}`);
      // Invalidate audit logs for the entity
      mutate(`/audit-log/master/${entityTypeId}/${entityId}`);
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error deleting entity file attachments:', error);
      throw error;
    }
  };

  return {
    deleteEntityFileAttachments,
    isDeleting: isMutating,
    error
  };
};

// Delete multiple file attachments by IDs
export const useDeleteMultipleFileAttachments = () => {
  const { trigger, isMutating, error } = useSWRMutation('/file-attachment', async (url, { arg }) => {
    const { fileIds } = arg;
    console.log('🔍 Frontend sending fileIds:', fileIds, 'Type:', typeof fileIds, 'Is Array:', Array.isArray(fileIds));
    
    // Get user ID from localStorage for audit tracking
    const savedUser = localStorage.getItem('user');
    let userId = null;
    
    if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(savedUser);
        userId = parsedUser.id?.toString() || null;
      } catch (parseError) {
        userId = null;
      }
    }
    
    const response = await fetch(`${API_BASE_URL}/file-attachment/bulk-delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(userId ? { 'user-id': userId } : {}),
        ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {})
      },
      body: JSON.stringify({ fileIds })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  });

  const deleteMultipleFileAttachments = async (fileIds, entityTypeId, entityId) => {
    try {
      const result = await trigger({ fileIds });
      
      // Invalidate related queries
      if (entityTypeId && entityId) {
        mutate(`/file-attachment/entity/${entityTypeId}/${entityId}`);
        // Invalidate audit logs for the entity
        mutate(`/audit-log/master/${entityTypeId}/${entityId}`);
      }
      mutate('/file-attachment');
      mutate('/file-attachment/stats');
      
      return result;
    } catch (error) {
      console.error('Error deleting multiple file attachments:', error);
      throw error;
    }
  };

  return {
    deleteMultipleFileAttachments,
    isDeleting: isMutating,
    error
  };
};

// Bulk create file attachments for an entity
export const useBulkCreateFileAttachments = () => {
  const { uploadMultipleFiles, isUploading, error } = useUploadMultipleFiles();

  const bulkCreateFileAttachments = async (fileAttachments, entityId) => {
    try {
      // Extract files from fileAttachments
      const files = fileAttachments.map(attachment => attachment.file).filter(Boolean);
      
      if (files.length === 0) {
        console.warn('No files to upload');
        return [];
      }

      // Get the first attachment to extract common data
      const firstAttachment = fileAttachments[0];
      const commonData = {
        entityTypeId: Number(firstAttachment.entityTypeId), // Ensure it's a number
        entityId: Number(entityId), // Ensure it's a number
        userId: firstAttachment.userId ? Number(firstAttachment.userId) : undefined,
        description: firstAttachment.description || '',
        isPrivate: firstAttachment.isPrivate || false,
        masterEntityTypeId: firstAttachment.masterEntityTypeId || 1 // Default to Document master
      };

      console.log('🔍 Uploading files with data:', {
        filesCount: files.length,
        commonData,
        firstAttachment
      });

      const results = await uploadMultipleFiles(files, commonData);
      return results;
    } catch (error) {
      console.error('Error bulk creating file attachments:', error);
      throw error;
    }
  };

  return {
    bulkCreateFileAttachments,
    isCreating: isUploading,
    error
  };
};

